import { useState, useEffect } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Building2, Search, Users, Layout, CreditCard, ChevronRight,
  Mail, Phone, Calendar, MoreVertical, Eye, Ban, CheckCircle, 
  AlertTriangle, Edit, Gift, Infinity, User, Trash2, Send, RefreshCw,
  UserPlus, Clock, Headphones
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import PhoneInput from "@/components/PhoneInput";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { FileText, Paperclip } from "lucide-react";

const BACKEND_URL = ((typeof window !== 'undefined' && window.location.hostname.includes('seatpilot.co.uk')) ? 'https://seatpilot.co.uk' : (process.env.REACT_APP_BACKEND_URL || 'https://seatpilot.co.uk'));
const API = `${BACKEND_URL}/api`;

// Plan types with default pricing
const PLAN_TYPES = [
  { id: "free_unlimited", name: "Free Unlimited", description: "£0 with unlimited access", price: 0, overage: 0, seats: -1, layouts: -1, events: -1 },
  { id: "free_trial", name: "Free Trial", description: "30-day trial with limits", price: 0, overage: 0, seats: 500, layouts: 1, events: 1 },
  { id: "pay_as_you_go", name: "Pay As You Go", description: "£0.50 per seat", price: 0, overage: 0.50, seats: -1, layouts: -1, events: -1 },
  { id: "monthly", name: "Monthly", description: "£50/month with seat allowance", price: 50, overage: 0.30, seats: 10000, layouts: -1, events: -1 },
  { id: "enterprise", name: "Enterprise", description: "Custom pricing", price: 0, overage: 0, seats: -1, layouts: -1, events: -1 }
];

export default function BackofficeCustomers() {
  const navigate = useNavigate();
  const { theme, user, permissions } = useOutletContext();
  const isSuperAdmin = user?.is_super_admin || user?.role === 'super_admin';
  const isDark = theme === 'dark';
  
  // Permission helpers - check granular permissions
  const hasCustomerRead = isSuperAdmin || permissions?.includes('customers');
  const hasCustomerWrite = isSuperAdmin || (permissions?.some(p => {
    // Check if permissions include write access (handled by backend as granular permissions)
    return typeof p === 'object' ? (p.key === 'customers' && p.write) : false;
  }) || permissions?.includes('customers_write'));
  // For now, if they have customers permission, we need to check backend for write access
  // The backend returns granular permissions, so 'customers' means at least read
  const canEditSubscriptions = isSuperAdmin;  // Only super admin can edit subscriptions
  
  const [companies, setCompanies] = useState([]);
  const [standaloneUsers, setStandaloneUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCompany, setSelectedCompany] = useState(null);

  // Edit Subscription Dialog
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [subForm, setSubForm] = useState({
    plan_id: "free_unlimited",
    monthly_price: 0,
    monthly_seat_allowance: -1,
    overage_per_seat: 0,
    max_events: -1,
    max_layouts: -1,
    billing_period: "monthly",
    expires_at: ""
  });
  const [savingSub, setSavingSub] = useState(false);

  // Free Unlimited Confirmation Dialog
  const [showFreeConfirm, setShowFreeConfirm] = useState(false);
  const [freeConfirmCompany, setFreeConfirmCompany] = useState(null);

  // Edit Customer Details Dialog
  const [showEditCustomer, setShowEditCustomer] = useState(false);
  const [customerForm, setCustomerForm] = useState({
    name: "",
    email: "",
    billing_email: "",
    phone: "",
    address: ""
  });
  const [savingCustomer, setSavingCustomer] = useState(false);

  // Edit Layouts Dialog
  const [showEditLayouts, setShowEditLayouts] = useState(false);
  const [customerLayouts, setCustomerLayouts] = useState([]);
  const [loadingLayouts, setLoadingLayouts] = useState(false);

  // Enterprise Enquiries
  const [enterpriseEnquiries, setEnterpriseEnquiries] = useState([]);

  // Delete Customer Dialog
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null); // { type: 'user' | 'company', data: ... }
  const [permanentDelete, setPermanentDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Send Reminder Dialog
  const [showReminderDialog, setShowReminderDialog] = useState(false);
  const [reminderTarget, setReminderTarget] = useState(null);
  const [reminderType, setReminderType] = useState("complete_signup");
  const [sendingReminder, setSendingReminder] = useState(false);

  // Active Tab
  const [activeTab, setActiveTab] = useState("companies");
  // Lifecycle filter for the customer grid: 'all' | 'active' | 'expiring' | 'expired'
  // Lets the backoffice slice the customer list by subscription health, the
  // same way we already do on the Subscriptions page.
  const [lifecycleFilter, setLifecycleFilter] = useState("all");

  // Custom Email Dialog
  const [showCustomEmailDialog, setShowCustomEmailDialog] = useState(false);
  const [emailTarget, setEmailTarget] = useState(null);
  const [emailForm, setEmailForm] = useState({
    template: "general",
    subject: "",
    message: ""
  });
  const [sendingCustomEmail, setSendingCustomEmail] = useState(false);

  // Block/Unblock Customer Dialog
  const [showBlockDialog, setShowBlockDialog] = useState(false);
  const [blockTarget, setBlockTarget] = useState(null);
  const [blockAction, setBlockAction] = useState("block"); // "block" or "unblock"
  const [blockReason, setBlockReason] = useState("");
  const [blocking, setBlocking] = useState(false);

  // Professional Email Templates for Customers
  const CUSTOMER_EMAIL_TEMPLATES = [
    {
      id: "general",
      name: "General Query",
      subject: "Regarding Your Account",
      message: "Dear {name},\n\nI hope this message finds you well.\n\n[Your message here]\n\nPlease don't hesitate to reach out if you have any questions.\n\nBest regards,\nThe Team"
    },
    {
      id: "offer",
      name: "Special Offer",
      subject: "Exclusive Offer Just For You!",
      message: "Dear {name},\n\nWe're excited to share an exclusive offer with you as a valued customer!\n\n[Offer details here]\n\n✨ Special Discount: [XX%] off your next [subscription/purchase]\n📅 Valid Until: [date]\n\nTo claim this offer, simply [action required].\n\nDon't miss out on this limited-time opportunity!\n\nBest regards,\nThe Team"
    },
    {
      id: "account",
      name: "Account Related",
      subject: "Important Information About Your Account",
      message: "Dear {name},\n\nWe're writing to inform you about an important update regarding your account.\n\n[Account details here]\n\nWhat you need to do:\n• [Action item 1]\n• [Action item 2]\n\nIf you have any questions or concerns, please don't hesitate to contact our support team.\n\nBest regards,\nThe Team"
    },
    {
      id: "invoice",
      name: "Invoice Related",
      subject: "Regarding Your Invoice",
      message: "Dear {name},\n\nWe're writing regarding your recent invoice.\n\n📄 Invoice Reference: [Invoice Number]\n💰 Amount: [Amount]\n📅 Due Date: [Date]\n\n[Additional details here]\n\nIf you have any questions about this invoice, please contact our billing team.\n\nBest regards,\nThe Team"
    },
    {
      id: "login",
      name: "Login/Security Related",
      subject: "Account Security Notice",
      message: "Dear {name},\n\nWe noticed some activity related to your account access.\n\n🔒 Activity Details:\n• [Details here]\n• Time: [timestamp]\n\nIf this was you, no action is needed.\n\nIf this wasn't you, please secure your account immediately by:\n1. Resetting your password\n2. Contacting our support team\n\nYour security is our priority.\n\nBest regards,\nThe Team"
    },
    {
      id: "subscription",
      name: "Subscription Update",
      subject: "Your Subscription Status Update",
      message: "Dear {name},\n\nWe wanted to update you on your subscription status.\n\n📊 Current Plan: [Plan Name]\n📅 Status: [Active/Expiring/etc.]\n\n[Details about the update]\n\nTo manage your subscription, visit your dashboard or contact us.\n\nBest regards,\nThe Team"
    },
    {
      id: "custom",
      name: "Custom Message",
      subject: "",
      message: ""
    }
  ];

  useEffect(() => {
    fetchCompanies();
  }, []);

  const fetchCompanies = async () => {
    setLoading(true);
    try {
      const [companiesRes, enquiriesRes] = await Promise.all([
        axios.get(`${API}/subscriptions/admin/companies`, { withCredentials: true }),
        axios.get(`${API}/backoffice/enterprise-enquiries`, { withCredentials: true }).catch(() => ({ data: { enquiries: [] } }))
      ]);
      setCompanies(companiesRes.data.companies || []);
      setStandaloneUsers(companiesRes.data.standalone_users || []);
      setEnterpriseEnquiries(enquiriesRes.data.enquiries || []);
    } catch (error) {
      toast.error("Failed to load customers");
    } finally {
      setLoading(false);
    }
  };

  // Delete customer (user or company)
  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/subscriptions/admin/customer`, {
        data: {
          user_id: deleteTarget.type === 'user' ? deleteTarget.data.user_id : null,
          company_id: deleteTarget.type === 'company' ? deleteTarget.data.company_id : null,
          permanently_delete: permanentDelete
        },
        withCredentials: true
      });
      toast.success("Customer deleted successfully");
      setShowDeleteDialog(false);
      setDeleteTarget(null);
      setPermanentDelete(false);
      fetchCompanies();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete customer");
    } finally {
      setDeleting(false);
    }
  };

  // Send reminder email
  const handleSendReminder = async () => {
    if (!reminderTarget) return;
    setSendingReminder(true);
    try {
      await axios.post(`${API}/subscriptions/admin/send-reminder`, {
        user_id: reminderTarget.user_id || null,
        company_id: reminderTarget.company_id || null,
        reminder_type: reminderType
      }, { withCredentials: true });
      toast.success("Reminder email sent successfully");
      setShowReminderDialog(false);
      setReminderTarget(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send reminder");
    } finally {
      setSendingReminder(false);
    }
  };

  // Open delete dialog for company
  const confirmDeleteCompany = (company) => {
    setDeleteTarget({ type: 'company', data: company });
    setShowDeleteDialog(true);
  };

  // Open delete dialog for standalone user
  const confirmDeleteUser = (user) => {
    setDeleteTarget({ type: 'user', data: user });
    setShowDeleteDialog(true);
  };

  // Open reminder dialog
  const openReminderDialog = (target) => {
    setReminderTarget(target);
    setReminderType(target.company_id ? "renew_subscription" : "complete_signup");
    setShowReminderDialog(true);
  };

  // Open Custom Email Dialog
  const openCustomEmailDialog = (target) => {
    setEmailTarget(target);
    const template = CUSTOMER_EMAIL_TEMPLATES.find(t => t.id === "general");
    const recipientName = target?.name || target?.company_name || "Customer";
    setEmailForm({
      template: "general",
      subject: template.subject,
      message: template.message.replace("{name}", recipientName)
    });
    setShowCustomEmailDialog(true);
  };

  // Handle email template change
  const handleEmailTemplateChange = (templateId) => {
    const template = CUSTOMER_EMAIL_TEMPLATES.find(t => t.id === templateId);
    if (template) {
      const recipientName = emailTarget?.name || emailTarget?.company_name || "Customer";
      setEmailForm({
        template: templateId,
        subject: template.subject.replace("{name}", recipientName),
        message: template.message.replace("{name}", recipientName)
      });
    }
  };

  // Send Custom Email
  const sendCustomEmail = async () => {
    if (!emailForm.subject || !emailForm.message) {
      toast.error("Please fill in subject and message");
      return;
    }
    setSendingCustomEmail(true);
    try {
      const payload = {
        subject: emailForm.subject,
        message: emailForm.message,
        template_id: emailForm.template
      };
      
      // Set the target based on type
      if (emailTarget?.company_id) {
        payload.company_id = emailTarget.company_id;
      } else if (emailTarget?.user_id) {
        payload.user_id = emailTarget.user_id;
      } else if (emailTarget?.email) {
        payload.email = emailTarget.email;
      }
      
      await axios.post(`${API}/subscriptions/admin/send-custom-email`, payload, { withCredentials: true });
      toast.success(`Email sent successfully`);
      setShowCustomEmailDialog(false);
      setEmailTarget(null);
      setEmailForm({ template: "general", subject: "", message: "" });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send email");
    } finally {
      setSendingCustomEmail(false);
    }
  };

  // Handle Block/Unblock Customer
  const handleBlockUnblock = async () => {
    if (!blockTarget || !blockReason.trim()) {
      toast.error("Please provide a reason");
      return;
    }
    
    setBlocking(true);
    try {
      const endpoint = blockAction === "block" 
        ? `${API}/subscriptions/admin/customers/${blockTarget.company_id}/block`
        : `${API}/subscriptions/admin/customers/${blockTarget.company_id}/unblock`;
      
      await axios.post(endpoint, { reason: blockReason.trim() }, { withCredentials: true });
      
      toast.success(`Customer ${blockAction === "block" ? "blocked" : "unblocked"} successfully`);
      setShowBlockDialog(false);
      setBlockTarget(null);
      setBlockReason("");
      fetchCompanies();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${blockAction} customer`);
    } finally {
      setBlocking(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP'
    }).format(amount || 0);
  };

  const getPlanBadge = (sub) => {
    if (!sub || !sub.plan_id) {
      return <span className={`px-2 py-1 rounded text-xs font-medium ${isDark ? 'bg-slate-700 text-slate-400' : 'bg-gray-100 text-gray-500'}`}>None</span>;
    }
    
    const badges = {
      free_trial: { label: "Free Trial", color: isDark ? "bg-blue-500/20 text-blue-400" : "bg-blue-100 text-blue-600" },
      pay_as_you_go: { label: "Pay As You Go", color: isDark ? "bg-green-500/20 text-green-400" : "bg-green-100 text-green-600" },
      monthly: { label: "Monthly", color: isDark ? "bg-amber-500/20 text-amber-400" : "bg-amber-100 text-amber-600" },
      monthly_yearly: { label: "Yearly", color: isDark ? "bg-orange-500/20 text-orange-400" : "bg-orange-100 text-orange-600" },
      enterprise: { label: "Enterprise", color: isDark ? "bg-purple-500/20 text-purple-400" : "bg-purple-100 text-purple-600" },
      enterprise_yearly: { label: "Enterprise (Yearly)", color: isDark ? "bg-purple-500/20 text-purple-400" : "bg-purple-100 text-purple-600" },
      free_unlimited: { label: "Free Unlimited", color: isDark ? "bg-violet-500/20 text-violet-400" : "bg-violet-100 text-violet-600" }
    };
    
    const badge = badges[sub.plan_id] || { label: sub.plan_id, color: isDark ? "bg-slate-700 text-slate-400" : "bg-gray-100 text-gray-500" };
    return <span className={`px-2 py-1 rounded text-xs font-medium ${badge.color}`}>{badge.label}</span>;
  };

  const filteredCompanies = companies.filter(company => {
    // Subscription-health filter (All / Active / Expiring / Expired)
    if (lifecycleFilter === "expired" && !company.subscription?.is_expired) return false;
    if (lifecycleFilter === "expiring" && !(company.subscription?.expiring_soon && !company.subscription?.is_expired)) return false;
    if (lifecycleFilter === "active") {
      const status = company.subscription?.status;
      if (company.subscription?.is_expired) return false;
      if (!["active", "trial", "trialing", "cancelling"].includes(status)) return false;
    }
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      company.name?.toLowerCase().includes(search) ||
      company.email?.toLowerCase().includes(search) ||
      company.owner_email?.toLowerCase().includes(search) ||
      company.company_id?.toLowerCase().includes(search) ||
      company.customer_id?.toLowerCase().includes(search)
    );
  });

  // ---- Subscription-health revenue summary ---------------------------------
  // Drives the four KPI cards above the customer grid so backoffice users can
  // quickly see total MRR, revenue at risk (expiring soon, no auto-renew),
  // and revenue lost (already expired) — answering "how much money walks out
  // the door if we don't act".
  const revenueSummary = (() => {
    const mrrFor = (sub) => Number(sub?.monthly_price || 0);
    let mrrActive = 0;
    let mrrAtRisk = 0;
    let mrrLost = 0;
    let cActive = 0;
    let cAtRisk = 0;
    let cLost = 0;
    for (const co of companies) {
      const sub = co?.subscription;
      if (!sub) continue;
      if (sub.is_expired) {
        mrrLost += mrrFor(sub);
        cLost += 1;
      } else if (sub.expiring_soon) {
        mrrAtRisk += mrrFor(sub);
        cAtRisk += 1;
        // Expiring-soon customers ARE still on an active plan today, count them in MRR
        mrrActive += mrrFor(sub);
        cActive += 1;
      } else if (["active", "trial", "trialing", "cancelling"].includes(sub.status)) {
        mrrActive += mrrFor(sub);
        cActive += 1;
      }
    }
    return { mrrActive, mrrAtRisk, mrrLost, cActive, cAtRisk, cLost };
  })();

  // Open edit subscription dialog
  const openEditSubscription = (company) => {
    const sub = company.subscription || {};
    setEditingCompany(company);
    setSubForm({
      plan_id: sub.plan_id || "free_trial",
      monthly_price: sub.monthly_price || 0,
      monthly_seat_allowance: sub.monthly_seat_allowance ?? -1,
      overage_per_seat: sub.overage_per_seat || 0,
      max_events: sub.max_events ?? -1,
      max_layouts: sub.max_layouts ?? -1,
      billing_period: sub.billing_period || "monthly",
      expires_at: sub.expires_at || ""
    });
    setShowEditDialog(true);
  };

  // Save subscription changes
  const saveSubscription = async () => {
    if (!editingCompany) return;
    setSavingSub(true);
    try {
      await axios.post(`${API}/subscriptions/admin/configure-plan`, {
        company_id: editingCompany.company_id,
        plan_id: subForm.plan_id,
        monthly_price: subForm.monthly_price,
        monthly_seat_allowance: subForm.monthly_seat_allowance,
        overage_per_seat: subForm.overage_per_seat,
        max_events: subForm.max_events === -1 ? null : subForm.max_events,
        max_layouts: subForm.max_layouts === -1 ? null : subForm.max_layouts,
        billing_period: subForm.billing_period,
        expires_at: subForm.expires_at || null,
        is_custom: subForm.plan_id === "free_unlimited" || subForm.plan_id === "enterprise"
      }, { withCredentials: true });
      
      toast.success("Subscription updated successfully");
      setShowEditDialog(false);
      setEditingCompany(null);
      fetchCompanies();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update subscription");
    } finally {
      setSavingSub(false);
    }
  };

  // Quick action: Give free unlimited with confirmation
  const confirmFreeUnlimited = (company) => {
    setFreeConfirmCompany(company);
    setShowFreeConfirm(true);
  };

  const giveUnlimitedFree = async () => {
    if (!freeConfirmCompany) return;
    try {
      await axios.post(`${API}/subscriptions/admin/configure-plan`, {
        company_id: freeConfirmCompany.company_id,
        plan_id: "free_unlimited",
        monthly_price: 0,
        monthly_seat_allowance: -1,
        overage_per_seat: 0,
        max_events: null,
        max_layouts: null,
        is_custom: true
      }, { withCredentials: true });
      toast.success(`Granted free unlimited access to ${freeConfirmCompany.name}`);
      setShowFreeConfirm(false);
      setFreeConfirmCompany(null);
      fetchCompanies();
    } catch (error) {
      toast.error("Failed to update subscription");
    }
  };

  // Edit Customer Details
  const openEditCustomer = (company) => {
    setEditingCompany(company);
    setCustomerForm({
      name: company.name || "",
      email: company.admin_email || company.email || "",
      billing_email: company.billing_email || "",
      phone: company.phone || "",
      address: company.address || ""
    });
    setShowEditCustomer(true);
  };

  const saveCustomerDetails = async () => {
    if (!editingCompany) return;
    setSavingCustomer(true);
    try {
      await axios.put(`${API}/subscriptions/admin/company/${editingCompany.company_id}`, {
        name: customerForm.name,
        billing_email: customerForm.billing_email,
        phone: customerForm.phone,
        address: customerForm.address
      }, { withCredentials: true });
      
      toast.success("Customer details updated");
      setShowEditCustomer(false);
      setEditingCompany(null);
      fetchCompanies();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update customer");
    } finally {
      setSavingCustomer(false);
    }
  };

  // Edit Layouts
  const openEditLayouts = async (company) => {
    setEditingCompany(company);
    setLoadingLayouts(true);
    setShowEditLayouts(true);
    try {
      const res = await axios.get(`${API}/backoffice/company/${company.company_id}/layouts`, { withCredentials: true });
      setCustomerLayouts(res.data.layouts || []);
    } catch (error) {
      toast.error("Failed to load layouts");
      setCustomerLayouts([]);
    } finally {
      setLoadingLayouts(false);
    }
  };

  const editLayout = (layoutId) => {
    // Open layout builder for this layout
    window.open(`/builder/${layoutId}?admin=true`, '_blank');
  };

  // Filter standalone users based on search
  const filteredStandaloneUsers = standaloneUsers.filter(user => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      user.name?.toLowerCase().includes(search) ||
      user.email?.toLowerCase().includes(search)
    );
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className={`text-2xl font-bold flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            <Building2 className="w-6 h-6 text-violet-500" />
            Customers
          </h1>
          <p className={`mt-1 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Manage all customers on the platform</p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" onClick={fetchCompanies}>
            <RefreshCw className="w-4 h-4 mr-1" /> Refresh
          </Button>
        </div>
      </div>

      {/* Live Enterprise Enquiries Banner */}
      {enterpriseEnquiries.length > 0 && (
        <div className="bg-gradient-to-r from-amber-50 to-orange-50 border-2 border-amber-300 rounded-xl p-4 shadow-sm" data-testid="enterprise-enquiries-banner">
          <div className="flex items-center gap-3 mb-3">
            <div className="flex items-center justify-center w-8 h-8 bg-amber-500 rounded-full animate-pulse">
              <CreditCard className="w-4 h-4 text-white" />
            </div>
            <div>
              <h3 className="font-bold text-amber-900">Enterprise Enquiries ({enterpriseEnquiries.length})</h3>
              <p className="text-xs text-amber-700">Potential revenue — respond quickly!</p>
            </div>
          </div>
          <div className="space-y-2">
            {enterpriseEnquiries.map((eq) => (
              <div 
                key={eq.ticket_id} 
                className="flex items-center justify-between p-3 bg-white/80 rounded-lg border border-amber-200 cursor-pointer hover:bg-white transition-colors"
                onClick={() => eq.company_id && navigate(`/backoffice/customers/${eq.company_id}`)}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-sm text-gray-900">{eq.user_name || eq.user_email || 'Unknown'}</span>
                    {eq.company_name && (
                      <span className="text-xs text-gray-500">({eq.company_name})</span>
                    )}
                    <span className={`px-1.5 py-0.5 text-[10px] rounded font-medium ${
                      eq.status === 'open' ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-700'
                    }`}>{eq.status}</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-0.5">
                    #{eq.ticket_number} &middot; {eq.created_at ? new Date(eq.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : 'N/A'}
                  </p>
                </div>
                <ChevronRight className="w-4 h-4 text-amber-400 flex-shrink-0" />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Revenue & Lifecycle KPIs — quick at-a-glance "where is the money?"
          for backoffice. MRR (active plans), At Risk (expiring soon, may
          not auto-renew), and Lost (already expired) are computed on the
          client from /admin/companies enrichment fields. */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="customer-revenue-kpis">
        <Card className={`p-4 cursor-pointer ${isDark ? 'bg-slate-800 border-slate-700 hover:border-violet-500/50' : 'bg-white border-gray-200 hover:border-violet-300'}`} onClick={() => setLifecycleFilter('all')}>
          <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>All Customers</p>
          <p className={`text-2xl font-bold mt-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>{companies.length}</p>
          <p className={`text-xs mt-1 ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>{revenueSummary.cActive + revenueSummary.cLost} with sub</p>
        </Card>
        <Card
          className={`p-4 cursor-pointer ${isDark ? 'bg-emerald-900/20 border-emerald-700/40 hover:border-emerald-500' : 'bg-emerald-50 border-emerald-200 hover:border-emerald-400'}`}
          onClick={() => setLifecycleFilter('active')}
          data-testid="kpi-mrr-active"
        >
          <p className={`text-xs ${isDark ? 'text-emerald-300' : 'text-emerald-700'}`}>Active MRR</p>
          <p className={`text-2xl font-bold mt-1 ${isDark ? 'text-emerald-100' : 'text-emerald-700'}`}>{formatCurrency(revenueSummary.mrrActive)}</p>
          <p className={`text-xs mt-1 ${isDark ? 'text-emerald-400/80' : 'text-emerald-600'}`}>{revenueSummary.cActive} customers</p>
        </Card>
        <Card
          className={`p-4 cursor-pointer ${isDark ? 'bg-amber-900/20 border-amber-700/40 hover:border-amber-500' : 'bg-amber-50 border-amber-200 hover:border-amber-400'}`}
          onClick={() => setLifecycleFilter('expiring')}
          data-testid="kpi-mrr-at-risk"
        >
          <p className={`text-xs flex items-center gap-1 ${isDark ? 'text-amber-300' : 'text-amber-700'}`}>
            <Clock className="w-3 h-3" /> Revenue at Risk
          </p>
          <p className={`text-2xl font-bold mt-1 ${isDark ? 'text-amber-100' : 'text-amber-700'}`}>{formatCurrency(revenueSummary.mrrAtRisk)}</p>
          <p className={`text-xs mt-1 ${isDark ? 'text-amber-400/80' : 'text-amber-600'}`}>{revenueSummary.cAtRisk} expiring ≤30d</p>
        </Card>
        <Card
          className={`p-4 cursor-pointer ${isDark ? 'bg-red-900/20 border-red-700/40 hover:border-red-500' : 'bg-red-50 border-red-200 hover:border-red-400'}`}
          onClick={() => setLifecycleFilter('expired')}
          data-testid="kpi-mrr-lost"
        >
          <p className={`text-xs flex items-center gap-1 ${isDark ? 'text-red-300' : 'text-red-700'}`}>
            <AlertTriangle className="w-3 h-3" /> Revenue Lost
          </p>
          <p className={`text-2xl font-bold mt-1 ${isDark ? 'text-red-100' : 'text-red-700'}`}>{formatCurrency(revenueSummary.mrrLost)}</p>
          <p className={`text-xs mt-1 ${isDark ? 'text-red-400/80' : 'text-red-600'}`}>{revenueSummary.cLost} expired</p>
        </Card>
      </div>

      {/* Lifecycle filter pills (clickable from the KPI cards above too) */}
      <div className="flex flex-wrap gap-2" data-testid="lifecycle-filter-row">
        {[
          { id: 'all', label: 'All', tone: 'violet' },
          { id: 'active', label: 'Active', tone: 'emerald' },
          { id: 'expiring', label: `Expiring Soon (${revenueSummary.cAtRisk})`, tone: 'amber' },
          { id: 'expired', label: `Expired (${revenueSummary.cLost})`, tone: 'red' },
        ].map(p => {
          const active = lifecycleFilter === p.id;
          const toneCls = active
            ? (p.tone === 'red' ? 'bg-red-600 text-white' :
               p.tone === 'amber' ? 'bg-amber-600 text-white' :
               p.tone === 'emerald' ? 'bg-emerald-600 text-white' : 'bg-violet-600 text-white')
            : (isDark ? 'bg-slate-800 border border-slate-700 text-slate-300 hover:bg-slate-700' : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50');
          return (
            <button
              key={p.id}
              onClick={() => setLifecycleFilter(p.id)}
              data-testid={`lifecycle-pill-${p.id}`}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${toneCls}`}
            >
              {p.label}
            </button>
          );
        })}
      </div>

      {/* Tab Navigation */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
          <TabsList className={isDark ? 'bg-slate-800' : 'bg-gray-100'}>
            <TabsTrigger value="companies" className="gap-2">
              <Building2 className="w-4 h-4" />
              Companies ({companies.length})
            </TabsTrigger>
            <TabsTrigger value="signups" className="gap-2">
              <UserPlus className="w-4 h-4" />
              Pending Signups ({standaloneUsers.length})
            </TabsTrigger>
          </TabsList>
          
          {/* Search */}
          <div className="relative flex-1 max-w-md">
            <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${isDark ? 'text-slate-500' : 'text-gray-400'}`} />
            <Input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search customers..."
              className={`pl-9 ${isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
            />
          </div>
        </div>

        {/* Companies Tab */}
        <TabsContent value="companies">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-500"></div>
            </div>
          ) : filteredCompanies.length === 0 ? (
            <Card className={`p-12 text-center ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}`}>
              <Building2 className={`w-16 h-16 mx-auto mb-4 ${isDark ? 'text-slate-600' : 'text-gray-300'}`} />
              <p className={isDark ? 'text-slate-400' : 'text-gray-500'}>No companies found</p>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {filteredCompanies.map((company) => (
                <Card 
                  key={company.company_id} 
                  className={`p-5 hover:border-violet-500/50 transition-all hover:shadow-lg cursor-pointer relative overflow-hidden ${
                    isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'
                  } ${company.is_blocked ? 'border-red-500/30' : ''} ${
                    company.subscription?.is_expired ? (isDark ? 'border-red-500/40 bg-red-900/10' : 'border-red-300 bg-red-50/50') :
                    company.subscription?.expiring_soon ? (isDark ? 'border-amber-500/40 bg-amber-900/10' : 'border-amber-300 bg-amber-50/50') : ''
                  }`}
                  // Card click now NAVIGATES to the full Customer Details page.
                  // Quick actions are still available via the dropdown.
                  onClick={() => navigate(`/backoffice/customers/${company.company_id}`)}
                  data-testid={`customer-card-${company.company_id}`}
                >
                  {/* Status Indicator Strip */}
                  <div className={`absolute top-0 left-0 right-0 h-1 ${
                    company.is_blocked ? 'bg-red-500' : 
                    company.subscription?.status === 'active' ? 'bg-green-500' : 
                    company.subscription?.status === 'trial' || company.subscription?.status === 'trialing' ? 'bg-amber-500' :
                    company.subscription?.status === 'cancelled' ? 'bg-red-400' :
                    company.subscription?.status === 'cancelling' ? 'bg-orange-400' : 'bg-gray-400'
                  }`} />
                  
                  <div className="flex items-start justify-between pt-2">
                    <div className="flex items-center gap-3">
                      {/* Company Avatar/Logo */}
                      <div className={`w-14 h-14 rounded-xl flex items-center justify-center text-xl font-bold ${
                        company.is_blocked 
                          ? (isDark ? 'bg-red-500/20 text-red-400' : 'bg-red-100 text-red-600')
                          : (isDark ? 'bg-violet-600/20 text-violet-400' : 'bg-violet-100 text-violet-600')
                      }`}>
                        {company.logo_url ? (
                          <img src={`${BACKEND_URL}${company.logo_url}`} alt={company.name} className="w-full h-full rounded-xl object-cover" />
                        ) : (
                          company.name?.charAt(0).toUpperCase() || <Building2 className="w-7 h-7" />
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className={`font-semibold truncate ${isDark ? 'text-white' : 'text-gray-900'}`}>
                            {company.name || "Unnamed Company"}
                          </h3>
                          {company.is_blocked && (
                            <span className="px-1.5 py-0.5 text-[10px] font-medium bg-red-100 text-red-600 rounded-full flex-shrink-0">
                              BLOCKED
                            </span>
                          )}
                          {/* Lifecycle flags — match the Subscriptions page so
                              backoffice users get a consistent visual signal
                              of subscription health on either page. */}
                          {company.subscription?.is_expired && (
                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-semibold bg-red-100 text-red-700 border border-red-200 rounded-full flex-shrink-0">
                              <AlertTriangle className="w-2.5 h-2.5" /> EXPIRED
                            </span>
                          )}
                          {!company.subscription?.is_expired && company.subscription?.expiring_soon && (
                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-semibold bg-amber-100 text-amber-700 border border-amber-200 rounded-full flex-shrink-0">
                              <Clock className="w-2.5 h-2.5" /> {company.subscription.days_until_expiry}d LEFT
                            </span>
                          )}
                          {!company.is_blocked && !company.subscription?.is_expired && company.subscription?.status === 'cancelled' && (
                            <span className="px-1.5 py-0.5 text-[10px] font-medium bg-red-100 text-red-500 rounded-full flex-shrink-0">
                              CANCELLED
                            </span>
                          )}
                          {!company.is_blocked && company.subscription?.status === 'cancelling' && (
                            <span className="px-1.5 py-0.5 text-[10px] font-medium bg-orange-100 text-orange-600 rounded-full flex-shrink-0">
                              CANCELLING
                            </span>
                          )}
                        </div>
                        {/* Customer ID, email, plan badge — always visible. */}
                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                          <span className={`text-xs font-mono px-2 py-0.5 rounded ${isDark ? 'bg-violet-500/20 text-violet-400' : 'bg-violet-100 text-violet-600'}`}>
                            {company.customer_id || `SP-${company.company_id?.slice(-6).toUpperCase()}`}
                          </span>
                          {getPlanBadge(company.subscription)}
                        </div>
                        {(company.owner_email || company.email) && (
                          <p className={`text-xs mt-1 truncate flex items-center gap-1 ${isDark ? 'text-slate-400' : 'text-gray-500'}`} title={company.owner_email || company.email}>
                            <Mail className="w-3 h-3" />
                            {company.owner_email || company.email}
                          </p>
                        )}
                      </div>
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                        <Button variant="ghost" size="sm" className={`-mt-1 -mr-2 ${isDark ? 'text-slate-400 hover:text-white' : 'text-gray-400 hover:text-gray-600'}`}>
                          <MoreVertical className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent>
                        <DropdownMenuItem onClick={(e) => { e.stopPropagation(); openEditCustomer(company); }}>
                          <User className="w-4 h-4 mr-2" />
                          Edit Customer Details
                        </DropdownMenuItem>
                        {canEditSubscriptions && (
                          <DropdownMenuItem onClick={(e) => { e.stopPropagation(); openEditSubscription(company); }}>
                            <Edit className="w-4 h-4 mr-2" />
                            Edit Subscription
                          </DropdownMenuItem>
                        )}
                        {isSuperAdmin && (
                          <DropdownMenuItem onClick={(e) => { e.stopPropagation(); confirmFreeUnlimited(company); }}>
                            <Gift className="w-4 h-4 mr-2" />
                            Give Free Unlimited
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem onClick={(e) => { e.stopPropagation(); navigate(`/backoffice/customers/${company.company_id}`); }}>
                          <Eye className="w-4 h-4 mr-2" />
                          View Details
                        </DropdownMenuItem>
                        {canEditSubscriptions && (
                          <DropdownMenuItem onClick={(e) => { e.stopPropagation(); openEditLayouts(company); }}>
                            <Layout className="w-4 h-4 mr-2" />
                            Edit Layouts
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={(e) => { e.stopPropagation(); openReminderDialog({ company_id: company.company_id, email: company.admin_email || company.owner_email }); }}>
                          <Send className="w-4 h-4 mr-2" />
                          Send Reminder Email
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={(e) => { e.stopPropagation(); openCustomEmailDialog({ ...company, company_name: company.name }); }}>
                          <FileText className="w-4 h-4 mr-2" />
                          Send Custom Email
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        {company.is_blocked ? (
                          <DropdownMenuItem className="text-green-600" onClick={(e) => { 
                            e.stopPropagation(); 
                            setBlockTarget(company);
                            setBlockAction("unblock");
                            setBlockReason("");
                            setShowBlockDialog(true);
                          }}>
                            <CheckCircle className="w-4 h-4 mr-2" />
                            Unblock Customer
                          </DropdownMenuItem>
                        ) : (
                          <DropdownMenuItem className="text-amber-600" onClick={(e) => { 
                            e.stopPropagation(); 
                            setBlockTarget(company);
                            setBlockAction("block");
                            setBlockReason("");
                            setShowBlockDialog(true);
                          }}>
                            <Ban className="w-4 h-4 mr-2" />
                            Block Customer
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem className="text-red-500" onClick={(e) => { e.stopPropagation(); confirmDeleteCompany(company); }}>
                          <Trash2 className="w-4 h-4 mr-2" />
                          Delete Customer
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>

                  {/* Contact Info */}
                  <div className="mt-4 space-y-1.5">
                    <div className={`flex items-center gap-2 text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                      <Mail className="w-4 h-4 flex-shrink-0" />
                      <a 
                        href={`mailto:${company.admin_email || company.owner_email || company.email}`}
                        onClick={(e) => e.stopPropagation()}
                        className="truncate hover:text-violet-500 hover:underline transition-colors"
                      >
                        {company.admin_email || company.owner_email || company.email || "No email"}
                      </a>
                    </div>
                    {company.phone && (
                      <div className={`flex items-center gap-2 text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                        <Phone className="w-4 h-4 flex-shrink-0" />
                        <a 
                          href={`tel:${company.phone}`}
                          onClick={(e) => e.stopPropagation()}
                          className="truncate hover:text-green-500 hover:underline transition-colors"
                        >
                          {company.phone}
                        </a>
                      </div>
                    )}
                  </div>

                  {/* Stats Row */}
                  <div className={`mt-4 pt-4 border-t grid grid-cols-4 gap-2 ${isDark ? 'border-slate-700' : 'border-gray-100'}`}>
                    <div className="text-center">
                      <p className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        {company.users?.length || 0}
                      </p>
                      <p className={`text-[10px] uppercase tracking-wider ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>Users</p>
                    </div>
                    <div className="text-center" title={`Draft: ${company.layouts_draft || 0} | Published: ${company.layouts_published || 0} | Linked: ${company.layouts_linked || 0}`}>
                      <p className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        {company.layouts_count || 0}
                      </p>
                      <p className={`text-[10px] uppercase tracking-wider ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>
                        {company.layouts_count > 0 ? (
                          <span>{company.layouts_published || 0}P/{company.layouts_draft || 0}D</span>
                        ) : "Layouts"}
                      </p>
                    </div>
                    <div className="text-center">
                      <p className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        {company.total_bookings || 0}
                      </p>
                      <p className={`text-[10px] uppercase tracking-wider ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>Bookings</p>
                    </div>
                    <div className="text-center">
                      <p className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        {formatCurrency(company.total_revenue || 0)}
                      </p>
                      <p className={`text-[10px] uppercase tracking-wider ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>Revenue</p>
                    </div>
                  </div>

                  {/* Footer with Quick Actions */}
                  <div className={`mt-4 pt-3 border-t flex items-center justify-between ${isDark ? 'border-slate-700' : 'border-gray-100'}`}>
                    <div className={`flex items-center gap-1 text-xs ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>
                      <Clock className="w-3 h-3" />
                      <span>Joined {formatDate(company.created_at)}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0"
                        onClick={(e) => { e.stopPropagation(); navigate(`/backoffice/customers/${company.company_id}`); }}
                        title="View Details"
                      >
                        <Eye className="w-3.5 h-3.5" />
                      </Button>
                      {canEditSubscriptions && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 w-7 p-0"
                          onClick={(e) => { e.stopPropagation(); openEditSubscription(company); }}
                          title="Edit Subscription"
                        >
                          <Edit className="w-3.5 h-3.5" />
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0"
                        onClick={(e) => { e.stopPropagation(); openCustomEmailDialog({ ...company, company_name: company.name }); }}
                        title="Send Email"
                      >
                        <Mail className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>

                  {/* Expanded Details */}
                  {selectedCompany?.company_id === company.company_id && (
                    <div className={`mt-4 pt-4 border-t space-y-4 ${isDark ? 'border-slate-700' : 'border-gray-200'}`}>
                      {/* Subscription Details */}
                      <div className={`p-3 rounded-lg ${isDark ? 'bg-slate-700/50' : 'bg-gray-50'}`}>
                        <h4 className={`text-xs font-semibold uppercase tracking-wider mb-3 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                          Subscription Details
                        </h4>
                        <div className="grid grid-cols-2 gap-3 text-sm">
                          <div>
                            <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>Status</p>
                            <p className={`font-medium flex items-center gap-1.5 ${
                              company.is_blocked ? 'text-red-500' : 
                              company.subscription?.status === 'cancelled' ? 'text-red-500' :
                              company.subscription?.status === 'cancelling' ? 'text-orange-500' :
                              company.subscription?.status === 'active' ? 'text-green-500' :
                              company.subscription?.status === 'trial' || company.subscription?.status === 'trialing' ? 'text-amber-500' :
                              'text-gray-500'
                            }`}>
                              <span className={`w-2 h-2 rounded-full ${
                                company.is_blocked ? 'bg-red-500' : 
                                company.subscription?.status === 'cancelled' ? 'bg-red-500' :
                                company.subscription?.status === 'cancelling' ? 'bg-orange-500' :
                                company.subscription?.status === 'active' ? 'bg-green-500' :
                                company.subscription?.status === 'trial' || company.subscription?.status === 'trialing' ? 'bg-amber-500' :
                                'bg-gray-400'
                              }`} />
                              {company.is_blocked ? 'Blocked' : 
                               company.subscription?.status === 'cancelled' ? 'Cancelled' :
                               company.subscription?.status === 'cancelling' ? 'Cancelling' :
                               company.subscription?.status === 'active' ? 'Active' :
                               company.subscription?.status === 'trial' || company.subscription?.status === 'trialing' ? 'Trial' :
                               company.subscription?.status || 'No Plan'}
                            </p>
                          </div>
                          <div>
                            <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>Monthly Price</p>
                            <p className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                              {formatCurrency(company.subscription?.monthly_price || 0)}/mo
                            </p>
                          </div>
                          <div>
                            <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>Seat Allowance</p>
                            <p className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                              {company.subscription?.monthly_seat_allowance === -1 || !company.subscription?.monthly_seat_allowance 
                                ? <span className="flex items-center gap-1"><Infinity className="w-4 h-4" /> Unlimited</span>
                                : company.subscription?.monthly_seat_allowance?.toLocaleString()}
                            </p>
                          </div>
                          <div>
                            <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>Plan Type</p>
                            <p className={`font-medium capitalize ${isDark ? 'text-white' : 'text-gray-900'}`}>
                              {company.subscription?.plan_name || company.subscription?.plan_id?.replace(/_/g, ' ') || 'None'}
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Block Reason if blocked */}
                      {company.is_blocked && company.block_reason && (
                        <div className={`p-3 rounded-lg border ${isDark ? 'bg-red-500/10 border-red-500/30' : 'bg-red-50 border-red-200'}`}>
                          <p className={`text-xs font-medium ${isDark ? 'text-red-400' : 'text-red-600'}`}>Block Reason:</p>
                          <p className={`text-sm mt-1 ${isDark ? 'text-red-300' : 'text-red-700'}`}>{company.block_reason}</p>
                        </div>
                      )}

                      {/* Support Tickets Summary */}
                      <div className={`p-3 rounded-lg ${isDark ? 'bg-slate-700/50' : 'bg-gray-50'}`}>
                        <div className="flex items-center justify-between mb-2">
                          <h4 className={`text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                            Support Tickets
                          </h4>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 text-xs gap-1"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/backoffice/support?company=${company.company_id}`);
                            }}
                          >
                            <Eye className="w-3 h-3" /> View All
                          </Button>
                        </div>
                        <div className="flex gap-4 text-sm">
                          <div className="flex items-center gap-1.5">
                            <div className="w-2 h-2 rounded-full bg-yellow-500" />
                            <span className={isDark ? 'text-slate-300' : 'text-gray-600'}>
                              {company.open_tickets || 0} Open
                            </span>
                          </div>
                          <div className="flex items-center gap-1.5">
                            <div className="w-2 h-2 rounded-full bg-green-500" />
                            <span className={isDark ? 'text-slate-300' : 'text-gray-600'}>
                              {company.resolved_tickets || 0} Resolved
                            </span>
                          </div>
                        </div>
                      </div>
                      
                      {/* Action Buttons */}
                      <div className="flex gap-2">
                        {canEditSubscriptions && (
                          <Button
                            onClick={(e) => {
                              e.stopPropagation();
                              openEditSubscription(company);
                            }}
                            className="flex-1"
                            variant="outline"
                            size="sm"
                          >
                            <Edit className="w-4 h-4 mr-1" />
                            Edit Subscription
                          </Button>
                        )}
                        <Button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/backoffice/customers/${company.company_id}`);
                          }}
                          className="flex-1 bg-violet-600 hover:bg-violet-700"
                          size="sm"
                        >
                          View Full Details
                          <ChevronRight className="w-4 h-4 ml-1" />
                        </Button>
                      </div>
                    </div>
                  )}
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Pending Signups Tab */}
        <TabsContent value="signups">
          <Card className={`p-6 ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}`}>
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>Pending Signups ({standaloneUsers.length})</h3>
                <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Users who signed up but have not completed company setup</p>
              </div>
            </div>

            {filteredStandaloneUsers.length === 0 ? (
              <div className="text-center py-12">
                <UserPlus className={`w-12 h-12 mx-auto mb-3 ${isDark ? 'text-slate-600' : 'text-gray-300'}`} />
                <p className={isDark ? 'text-slate-400' : 'text-gray-500'}>No pending signups</p>
                <p className={`text-sm mt-1 ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>All users have completed their company setup</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className={`border-b ${isDark ? 'border-slate-700' : 'border-gray-200'}`}>
                      <th className={`text-left py-3 px-4 text-sm font-medium ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>User</th>
                      <th className={`text-left py-3 px-4 text-sm font-medium ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Email</th>
                      <th className={`text-left py-3 px-4 text-sm font-medium ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Signed Up</th>
                      <th className={`text-left py-3 px-4 text-sm font-medium ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Status</th>
                      <th className={`text-left py-3 px-4 text-sm font-medium ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredStandaloneUsers.map(user => (
                      <tr key={user.user_id} className={`border-b ${isDark ? 'border-slate-700 hover:bg-slate-700/50' : 'border-gray-100 hover:bg-gray-50'}`}>
                        <td className={`py-3 px-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                          <div className="flex items-center gap-2">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${isDark ? 'bg-amber-500/20' : 'bg-amber-100'}`}>
                              <User className={`w-4 h-4 ${isDark ? 'text-amber-400' : 'text-amber-600'}`} />
                            </div>
                            <span className="font-medium">{user.name || user.email?.split('@')[0] || 'Unknown'}</span>
                          </div>
                        </td>
                        <td className={`py-3 px-4 ${isDark ? 'text-slate-300' : 'text-gray-600'}`}>
                          {user.email}
                        </td>
                        <td className={`py-3 px-4 text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                          <div className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {user.created_at ? formatDate(user.created_at) : '-'}
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            user.email_verified 
                              ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' 
                              : 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
                          }`}>
                            {user.email_verified ? 'Email Verified' : 'Email Pending'}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex gap-1">
                            <Button variant="ghost" size="sm" onClick={() => openReminderDialog({ user_id: user.user_id, email: user.email })} title="Send Reminder">
                              <Send className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => openCustomEmailDialog({ ...user, name: user.name || user.email?.split('@')[0] })} title="Send Custom Email">
                              <FileText className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="sm" className="text-red-500" onClick={() => confirmDeleteUser(user)} title="Delete User">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </TabsContent>
      </Tabs>

      {/* Edit Subscription Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit Subscription - {editingCompany?.name}</DialogTitle>
            <DialogDescription>Configure subscription for this company</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 max-h-[60vh] overflow-y-auto py-4">
            <div>
              <Label>Plan Type</Label>
              <Select value={subForm.plan_id} onValueChange={(v) => {
                // Find the selected plan's default values
                const selectedPlan = PLAN_TYPES.find(p => p.id === v);
                if (selectedPlan) {
                  setSubForm({
                    ...subForm,
                    plan_id: v,
                    monthly_price: selectedPlan.price,
                    overage_per_seat: selectedPlan.overage,
                    monthly_seat_allowance: selectedPlan.seats,
                    max_layouts: selectedPlan.layouts,
                    max_events: selectedPlan.events
                  });
                }
              }}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PLAN_TYPES.map(plan => (
                    <SelectItem key={plan.id} value={plan.id}>
                      <div className="flex flex-col">
                        <span>{plan.name}</span>
                        <span className="text-xs text-slate-500">{plan.description}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Monthly Price (£)</Label>
                <Input 
                  type="number" 
                  min="0"
                  value={subForm.monthly_price} 
                  onChange={(e) => setSubForm({...subForm, monthly_price: parseFloat(e.target.value) || 0})} 
                />
              </div>
              <div>
                <Label>Overage Per Seat (£)</Label>
                <Input 
                  type="number" 
                  min="0"
                  step="0.01"
                  value={subForm.overage_per_seat} 
                  onChange={(e) => setSubForm({...subForm, overage_per_seat: parseFloat(e.target.value) || 0})} 
                />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Monthly Seat Allowance</Label>
                <div className="flex items-center gap-2">
                  <Switch 
                    checked={subForm.monthly_seat_allowance === -1}
                    onCheckedChange={(checked) => setSubForm({...subForm, monthly_seat_allowance: checked ? -1 : 1000})}
                  />
                  <span className="text-sm text-slate-500">Unlimited</span>
                </div>
              </div>
              {subForm.monthly_seat_allowance !== -1 && (
                <Input 
                  type="number" 
                  min="1"
                  value={subForm.monthly_seat_allowance} 
                  onChange={(e) => setSubForm({...subForm, monthly_seat_allowance: parseInt(e.target.value) || 1000})} 
                />
              )}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label>Max Layouts</Label>
                  <div className="flex items-center gap-2">
                    <Switch 
                      checked={subForm.max_layouts === -1}
                      onCheckedChange={(checked) => setSubForm({...subForm, max_layouts: checked ? -1 : 10})}
                    />
                    <span className="text-xs text-slate-500">∞</span>
                  </div>
                </div>
                {subForm.max_layouts !== -1 && (
                  <Input 
                    type="number" 
                    min="1"
                    value={subForm.max_layouts} 
                    onChange={(e) => setSubForm({...subForm, max_layouts: parseInt(e.target.value) || 10})} 
                  />
                )}
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label>Max Events</Label>
                  <div className="flex items-center gap-2">
                    <Switch 
                      checked={subForm.max_events === -1}
                      onCheckedChange={(checked) => setSubForm({...subForm, max_events: checked ? -1 : 50})}
                    />
                    <span className="text-xs text-slate-500">∞</span>
                  </div>
                </div>
                {subForm.max_events !== -1 && (
                  <Input 
                    type="number" 
                    min="1"
                    value={subForm.max_events} 
                    onChange={(e) => setSubForm({...subForm, max_events: parseInt(e.target.value) || 50})} 
                  />
                )}
              </div>
            </div>

            {/* Billing Period */}
            <div>
              <Label>Billing Period (Rolling)</Label>
              <Select value={subForm.billing_period} onValueChange={(v) => setSubForm({...subForm, billing_period: v})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="monthly">Monthly (Rolling)</SelectItem>
                  <SelectItem value="yearly">Yearly (Rolling)</SelectItem>
                  <SelectItem value="one_time">One-Time / No Renewal</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Expiry Date (for trial/test plans) */}
            <div>
              <Label>Plan Expiry Date (Optional - for test/trial)</Label>
              <Input 
                type="date"
                value={subForm.expires_at ? subForm.expires_at.split('T')[0] : ''}
                onChange={(e) => setSubForm({...subForm, expires_at: e.target.value || null})}
              />
              <p className="text-xs text-slate-500 mt-1">Leave empty for no expiry. Plan will auto-renew based on billing period.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>Cancel</Button>
            <Button onClick={saveSubscription} disabled={savingSub}>
              {savingSub && <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Free Unlimited Confirmation Dialog */}
      <AlertDialog open={showFreeConfirm} onOpenChange={setShowFreeConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Gift className="w-5 h-5 text-violet-500" />
              Grant Free Unlimited Access
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-3">
              <p>
                You are about to grant <strong className="text-violet-600">{freeConfirmCompany?.name}</strong> free unlimited access.
              </p>
              <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-sm">
                <strong>This will:</strong>
                <ul className="list-disc ml-4 mt-1 space-y-1">
                  <li>Set monthly price to £0</li>
                  <li>Remove all seat, layout, and event limits</li>
                  <li>Mark the plan as a custom/unlimited plan</li>
                </ul>
              </div>
              <p className="text-sm">Are you sure you want to proceed?</p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => { setShowFreeConfirm(false); setFreeConfirmCompany(null); }}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction onClick={giveUnlimitedFree} className="bg-violet-600 hover:bg-violet-700">
              Yes, Grant Free Access
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Edit Customer Details Dialog */}
      <Dialog open={showEditCustomer} onOpenChange={setShowEditCustomer}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <User className="w-5 h-5 text-violet-500" />
              Edit Customer Details
            </DialogTitle>
            <DialogDescription>
              Update {editingCompany?.name}&apos;s information
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Company Name</Label>
              <Input
                value={customerForm.name}
                onChange={(e) => setCustomerForm({...customerForm, name: e.target.value})}
                placeholder="Company name"
              />
            </div>
            <div>
              <Label>Primary Email (Read Only)</Label>
              <Input
                value={customerForm.email}
                disabled
                className="bg-gray-100"
              />
              <p className="text-xs text-gray-500 mt-1">Primary email cannot be changed from here</p>
            </div>
            <div>
              <Label>Billing Email</Label>
              <Input
                value={customerForm.billing_email}
                onChange={(e) => setCustomerForm({...customerForm, billing_email: e.target.value})}
                placeholder="billing@company.com"
              />
            </div>
            <div>
              <Label>Phone</Label>
              <PhoneInput
                value={customerForm.phone}
                onChange={(phone) => setCustomerForm({...customerForm, phone})}
                autoDetectCountry={true}
              />
            </div>
            <div>
              <Label>Address</Label>
              <Input
                value={customerForm.address}
                onChange={(e) => setCustomerForm({...customerForm, address: e.target.value})}
                placeholder="Business address"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditCustomer(false)}>Cancel</Button>
            <Button onClick={saveCustomerDetails} disabled={savingCustomer}>
              {savingCustomer && <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Layouts Dialog */}
      <Dialog open={showEditLayouts} onOpenChange={setShowEditLayouts}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Layout className="w-5 h-5 text-violet-500" />
              {editingCompany?.name}&apos;s Layouts
            </DialogTitle>
            <DialogDescription>
              View and edit customer layouts
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 max-h-[400px] overflow-y-auto">
            {/* Layout Summary */}
            {!loadingLayouts && customerLayouts.length > 0 && (
              <div className="mb-4 p-3 bg-gray-50 rounded-lg grid grid-cols-4 gap-4 text-center">
                <div>
                  <p className="text-lg font-semibold text-gray-900">{customerLayouts.length}</p>
                  <p className="text-xs text-gray-500">Total</p>
                </div>
                <div>
                  <p className="text-lg font-semibold text-green-600">
                    {customerLayouts.filter(l => l.is_published || l.status === 'published').length}
                  </p>
                  <p className="text-xs text-gray-500">Published</p>
                </div>
                <div>
                  <p className="text-lg font-semibold text-amber-600">
                    {customerLayouts.filter(l => l.status === 'draft' || !l.is_published).length}
                  </p>
                  <p className="text-xs text-gray-500">Draft</p>
                </div>
                <div>
                  <p className="text-lg font-semibold text-blue-600">
                    {customerLayouts.filter(l => l.event_id || l.linked_events?.length > 0).length}
                  </p>
                  <p className="text-xs text-gray-500">Linked</p>
                </div>
              </div>
            )}
            
            {loadingLayouts ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-500"></div>
              </div>
            ) : customerLayouts.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Layout className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>No layouts found</p>
              </div>
            ) : (
              <div className="space-y-2">
                {customerLayouts.map(layout => (
                  <div 
                    key={layout.layout_id}
                    className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50"
                  >
                    <div>
                      <p className="font-medium text-gray-900">{layout.name}</p>
                      <p className="text-sm text-gray-500">
                        {layout.venue_name || 'No venue'} • {layout.objects_count || 0} objects • {layout.total_seats || 0} seats
                      </p>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => editLayout(layout.layout_id)}
                    >
                      <Edit className="w-4 h-4 mr-1" /> Edit
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditLayouts(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Customer Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="w-5 h-5" />
              Delete {deleteTarget?.type === 'company' ? 'Customer' : 'User'}
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-3">
              <p>
                You are about to delete <strong className="text-red-600">
                  {deleteTarget?.type === 'company' ? deleteTarget?.data?.name : deleteTarget?.data?.email}
                </strong>.
              </p>
              
              <div className={`p-3 rounded-lg ${permanentDelete ? 'bg-red-50 border border-red-200 text-red-800' : 'bg-amber-50 border border-amber-200 text-amber-800'} text-sm`}>
                <div className="flex items-center gap-2 mb-2">
                  <input
                    type="checkbox"
                    id="permanentDelete"
                    checked={permanentDelete}
                    onChange={(e) => setPermanentDelete(e.target.checked)}
                    className="rounded border-gray-300"
                  />
                  <label htmlFor="permanentDelete" className="font-medium cursor-pointer">
                    Permanently delete all data
                  </label>
                </div>
                <p className="text-xs ml-5">
                  {permanentDelete 
                    ? 'This will permanently remove all data including layouts, events, and subscriptions. This cannot be undone.'
                    : 'The customer will be marked as deleted and can sign up again with the same email.'}
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => { setShowDeleteDialog(false); setDeleteTarget(null); setPermanentDelete(false); }}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} disabled={deleting} className="bg-red-600 hover:bg-red-700">
              {deleting && <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />}
              {permanentDelete ? 'Permanently Delete' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Send Reminder Dialog */}
      <Dialog open={showReminderDialog} onOpenChange={setShowReminderDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="w-5 h-5 text-violet-500" />
              Send Reminder Email
            </DialogTitle>
            <DialogDescription>
              Send a reminder email to {reminderTarget?.email}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Reminder Type</Label>
              <Select value={reminderType} onValueChange={setReminderType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="complete_signup">Complete Signup</SelectItem>
                  <SelectItem value="verify_email">Verify Email</SelectItem>
                  <SelectItem value="renew_subscription">Renew Subscription</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className={`p-3 rounded-lg text-sm ${isDark ? 'bg-slate-700 text-slate-300' : 'bg-gray-100 text-gray-600'}`}>
              {reminderType === 'complete_signup' && 'Sends a friendly reminder to complete the signup process and create their company.'}
              {reminderType === 'verify_email' && 'Sends a reminder to verify their email address.'}
              {reminderType === 'renew_subscription' && 'Sends a reminder about subscription renewal.'}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReminderDialog(false)}>Cancel</Button>
            <Button onClick={handleSendReminder} disabled={sendingReminder}>
              {sendingReminder && <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />}
              Send Reminder
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Block/Unblock Customer Dialog */}
      <AlertDialog open={showBlockDialog} onOpenChange={setShowBlockDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className={blockAction === "block" ? "text-amber-600" : "text-green-600"}>
              {blockAction === "block" ? "Block Customer" : "Unblock Customer"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {blockAction === "block" 
                ? `This will prevent "${blockTarget?.name}" from accessing their account. All active sessions will be terminated.`
                : `This will restore access for "${blockTarget?.name}". They will be able to log in again.`
              }
            </AlertDialogDescription>
          </AlertDialogHeader>
          
          <div className="py-4">
            <Label className="mb-2 block">
              Reason for {blockAction === "block" ? "blocking" : "unblocking"} <span className="text-red-500">*</span>
            </Label>
            <Textarea
              value={blockReason}
              onChange={(e) => setBlockReason(e.target.value)}
              placeholder={blockAction === "block" 
                ? "e.g., Violation of terms of service, Payment issues, Suspicious activity..." 
                : "e.g., Issue resolved, Payment confirmed, Account verified..."
              }
              rows={3}
            />
            <p className={`text-xs mt-2 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
              This reason will be recorded in the customer&apos;s activity history.
            </p>
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => {
              setShowBlockDialog(false);
              setBlockTarget(null);
              setBlockReason("");
            }}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleBlockUnblock}
              disabled={!blockReason.trim() || blocking}
              className={blockAction === "block" ? "bg-amber-600 hover:bg-amber-700" : "bg-green-600 hover:bg-green-700"}
            >
              {blocking && <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />}
              {blockAction === "block" ? "Block Customer" : "Unblock Customer"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Custom Email Dialog */}
      <Dialog open={showCustomEmailDialog} onOpenChange={setShowCustomEmailDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-violet-500" />
              Send Custom Email
            </DialogTitle>
            <DialogDescription>
              Send a professional email to {emailTarget?.name || emailTarget?.company_name || 'Customer'}
              {emailTarget?.admin_email || emailTarget?.owner_email || emailTarget?.email ? 
                ` (${emailTarget?.admin_email || emailTarget?.owner_email || emailTarget?.email})` : ''}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Template Selection */}
            <div>
              <Label>Email Template</Label>
              <Select value={emailForm.template} onValueChange={handleEmailTemplateChange}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CUSTOMER_EMAIL_TEMPLATES.map(template => (
                    <SelectItem key={template.id} value={template.id}>
                      <div className="flex items-center gap-2">
                        <FileText className="w-4 h-4" />
                        {template.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Subject */}
            <div>
              <Label>Subject</Label>
              <Input
                value={emailForm.subject}
                onChange={(e) => setEmailForm({ ...emailForm, subject: e.target.value })}
                placeholder="Email subject"
              />
            </div>

            {/* Message */}
            <div>
              <Label>Message</Label>
              <Textarea
                value={emailForm.message}
                onChange={(e) => setEmailForm({ ...emailForm, message: e.target.value })}
                placeholder="Your message..."
                rows={10}
                className="font-mono text-sm"
              />
            </div>

            {/* Attachments (Coming Soon) */}
            <div className={`p-3 rounded-lg ${isDark ? 'bg-slate-700/50' : 'bg-gray-50'}`}>
              <div className="flex items-center gap-2 mb-2">
                <Paperclip className="w-4 h-4" />
                <span className={`text-sm font-medium ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>
                  Attachments
                </span>
                <span className="text-xs px-2 py-0.5 rounded bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
                  Coming Soon
                </span>
              </div>
              <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                File attachment support (invoices, payment receipts, etc.) will be available in a future update.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowCustomEmailDialog(false);
              setEmailTarget(null);
              setEmailForm({ template: "general", subject: "", message: "" });
            }}>
              Cancel
            </Button>
            <Button onClick={sendCustomEmail} disabled={sendingCustomEmail || !emailForm.subject || !emailForm.message}>
              {sendingCustomEmail && <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />}
              Send Email
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
